import { motion } from 'framer-motion';
import { ArrowUpRight, MapPin } from 'lucide-react';
import { HamburgerMenu } from '../components/HamburgerMenu';
import { NewsletterSignup } from '../components/NewsletterSignup';
import { SEO } from '../components/SEO';
import { ImageSlot } from '../components/ImageSlot';
const SITE_URL = 'https://www.groundedlivermore.com';
type Maker = {
  name: string;
  location: string;
  url: string;
  blurb: string;
};
const makers: Maker[] = [{
  name: 'Butter & Batter',
  location: 'Livermore, CA',
  url: 'https://butterandbatter.com',
  blurb: "A Livermore cookie bar and dessert-catering favorite, Butter & Batter turns out the kind of cookies that don't survive the drive home. We keep them on our case as the perfect sweet alongside your coffee."
}, {
  name: "Bubb's Bagels",
  location: 'Livermore, CA',
  url: 'https://bubbsbagels.com',
  blurb: "Bubbs' Bagels & Coffee, over on Holmes Street, is a fellow Livermore small business and a friend of the shop. Our bagels and cream cheese are fresh from Bubbs' oven, and we couldn't be more proud to serve them."
}, {
  name: 'Ginger Lab',
  location: 'Mountain View, CA',
  url: 'https://drinkgingerlab.com',
  blurb: 'Small-batch, seriously delicious craft ginger beer from Mountain View - bright, punchy, and a great non-coffee option or afternoon reset.'
}, {
  name: 'Living Flora',
  location: 'San Mateo, CA',
  url: 'https://drinklivingflora.com',
  blurb: "A living, probiotic sparkling water kefir - light, fizzy, and gut-friendly, for when you want something refreshing that's doing you a little good."
}, {
  name: 'JTM Seasoning',
  location: 'Livermore, CA',
  url: 'https://jtminthekitchen.com',
  blurb: 'JTM is the small, founder-run spice brand of Jeremy Troupe-Masi - a Livermore local and passionate cook who built a range of globally-inspired blends. His Everything Seasoning is the finishing touch across a lot of our menu, and his homemade chili oil is just quite frankly, EVERYTHING.'
}];
const collectionSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Local Makers on Our Shelf',
  description: "Beyond coffee, Grounded curates local makers - Butter & Batter cookies, Ginger Lab ginger beer, Living Flora water kefir, JTM seasoning, and Bubb's Bagels.",
  url: `${SITE_URL}/makers`,
  isPartOf: {
    '@type': 'WebSite',
    name: 'Grounded Livermore',
    url: SITE_URL
  },
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: makers.map((maker, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Brand',
        name: maker.name,
        description: maker.blurb,
        url: maker.url
      }
    }))
  }
};
export function Makers() {
  return <div className="min-h-screen bg-[#0a0a0a] text-white selection:bg-[#D97E3A] selection:text-white" data-id="element-521" data-mp="r531gajom78c">
      <SEO title="Local Makers We Carry - Livermore & the Bay Area | Grounded" description="Beyond coffee, Grounded curates local makers - Butter & Batter cookies, Ginger Lab ginger beer, Living Flora water kefir, JTM seasoning, and Bubb's Bagels. Taste local in Livermore, CA." canonical={`${SITE_URL}/makers`} structuredData={collectionSchema} data-id="element-522" data-mp="v5vab2on6a43" />
      <HamburgerMenu data-id="element-523" data-mp="21x0yw7lmly2b" />

      <main data-id="element-524" data-mp="21xu1cwdjy1en">
        {/* Header */}
        <header className="pt-32 pb-16 px-6 md:px-12 lg:px-24 max-w-5xl mx-auto" data-id="element-525" data-mp="1wyr6g4radbke">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          ease: 'easeOut'
        }} data-id="element-526" data-mp="1wwmtyyfbn48i">
            <div className="flex items-center gap-3 mb-6" data-id="element-527" data-mp="1nyktcjzi2dlr">
              <div className="h-px w-12 bg-[#D97E3A]" data-id="element-528" data-mp="1j09m8h0lah78" />
              <span className="text-[#D97E3A] font-inter font-medium tracking-widest text-xs uppercase" data-id="element-529" data-mp="1j1kq0gu1boyc">
                Local Delights
              </span>
            </div>

            <h1 className="font-bebas text-5xl md:text-7xl lg:text-8xl leading-[0.9] mb-8" data-id="element-530" data-mp="3de0r737z4cwc">
              Local Makers on Our Shelf
            </h1>

            <p className="font-inter text-lg text-white/70 leading-relaxed max-w-3xl border-l-2 border-[#D97E3A] pl-6" data-id="element-531" data-mp="2f1y7aue717wb">
              We curate our coffee from independent roasters - and we do the
              same with our local delights. The shelf and the case at Grounded
              are a rotating showcase of small-batch makers we believe in, most
              of them from Livermore and around the Bay. When you grab a cookie,
              a ginger beer, or a bottle of kefir here, you're supporting a
              neighbor's craft.
            </p>
          </motion.div>
        </header>

        {/* Maker list */}
        <section aria-label="Makers we carry" className="px-6 md:px-12 lg:px-24 max-w-5xl mx-auto pb-24" data-id="element-532" data-mp="1mdk8wdj709k8">
          <ul className="space-y-8" data-id="element-533" data-mp="18f2nimgrf95h">
            {makers.map((maker, i) => <motion.li key={maker.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06
          }} data-id="element-534" data-mp="1dbazxs3koo8y" data-mpi={i}>
                <article className="border border-white/10 bg-[#111] grid grid-cols-1 md:grid-cols-5 gap-0 overflow-hidden" data-id="element-535" data-mp="13q1l3h7l6fkf">
                  <ImageSlot label={`${maker.name} photo`} aspect="aspect-[4/3] md:aspect-auto md:h-full md:min-h-[220px]" className="md:col-span-2 border-0 md:border-r md:border-dashed md:border-white/15" data-id="element-536" data-mp="yrqcf9t12b3s" />

                  <div className="md:col-span-3 p-8 md:p-10" data-id="element-537" data-mp="1mh15s681t37w">
                    <div className="flex items-center gap-2 text-[#D97E3A] font-inter text-xs font-medium uppercase tracking-widest mb-3" data-id="element-538" data-mp="1hgwpohb1yai7">
                      <MapPin size={14} aria-hidden="true" data-id="element-539" data-mp="1vdjut7d5s6z7" />
                      {maker.location}
                    </div>

                    <h2 className="font-bebas text-3xl md:text-4xl leading-none mb-4" data-id="element-540" data-mp="1yhhox6s1rhje">
                      {maker.name}
                    </h2>

                    <p className="font-inter text-base text-white/65 leading-relaxed mb-6" data-id="element-541" data-mp="23gu7vgfss4gp">
                      {maker.blurb}
                    </p>

                    <a href={maker.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-bebas text-lg tracking-wide text-[#D97E3A] group" data-id="element-542" data-mp="2d5vqzfhn31yw">
                      VISIT {maker.name.toUpperCase()}
                      <ArrowUpRight size={18} aria-hidden="true" className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" data-id="element-543" data-mp="2r6g5w0kfodn4" />
                    </a>
                  </div>
                </article>
              </motion.li>)}
          </ul>
        </section>

        <NewsletterSignup data-id="element-544" data-mp="3f21nc1kucx3w" />

        <footer className="border-t border-white/10 py-12 text-center" data-id="element-545" data-mp="3f3cqq0adk2uh">
          <p className="font-bebas text-2xl text-white/50" data-id="element-546" data-mp="3a8yuxwrsvtpa">
            © {new Date().getFullYear()} GROUNDED LIVERMORE
          </p>
        </footer>
      </main>
    </div>;
}